import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AnimatePresence } from "framer-motion";
import { AuthProvider, useAuth } from "@/lib/auth";
import AuthCallback from "@/pages/AuthCallback";
import LandingPage from "@/pages/Landing";
import LoginPage from "@/pages/Login";
import DashboardPage from "@/pages/Dashboard";
import GeneratePage from "@/pages/Generate";
import CreativeBenchmarkPage from "@/pages/CreativeBenchmark";
import CalendarPage from "@/pages/Calendar";
import IdeasPage from "@/pages/Ideas";
import EmailMarketingPage from "@/pages/EmailMarketing";
import CompetitorAnalysisPage from "@/pages/CompetitorAnalysis";
import BillingPage from "@/pages/Billing";
import AutopilotPage from "@/pages/Autopilot";
import ContentManagerPage from "@/pages/ContentManager";
import ConnectionsPage from "@/pages/Connections";
import OnboardingPage from "@/pages/Onboarding";
import SupportPage from "@/pages/Support";
import FeedbackPage from "@/pages/Feedback";
import MediaLibraryPage from "@/pages/MediaLibrary";
import BrandLearningPage from "@/pages/BrandLearning";
import TeamPage from "@/pages/Team";
import BusinessProfilePage from "@/pages/BusinessProfile";
import SecurityPage from "@/pages/settings/Security";
import Login2FAPage from "@/pages/Login2FA";
import AdminGuard from "@/components/admin/AdminGuard";
import AdminOverviewPage from "@/pages/admin/AdminOverview";
import AdminCustomersPage from "@/pages/admin/AdminCustomers";
import AdminPlatformSetupPage from "@/pages/admin/AdminPlatformSetup";
import AdminOperationsPage from "@/pages/admin/AdminOperations";
import AdminSupportPage from "@/pages/admin/AdminSupport";
import AdminFeedbackPage from "@/pages/admin/AdminFeedback";
import AdminMetaChecklistPage from "@/pages/admin/AdminMetaChecklist";
import AdminLinkedInChecklistPage from "@/pages/admin/AdminLinkedInChecklist";
import PrivacyPage from "@/pages/legal/Privacy";
import TermsPage from "@/pages/legal/Terms";
import DataDeletionPage from "@/pages/legal/DataDeletion";
import SetPasswordPage from "@/pages/SetPassword";
import ResetPasswordPage from "@/pages/ResetPassword";
import ForgotPasswordPage from "@/pages/ForgotPassword";
import NotFoundPage from "@/pages/NotFound";
import AiSocialMediaManagementPage from "@/pages/marketing/AiSocialMediaManagement";
import SocialMediaSchedulerPage from "@/pages/marketing/SocialMediaScheduler";
import WhatsNewPublicPage from "@/pages/marketing/WhatsNewPublic";
import PricingPublicPage from "@/pages/marketing/Pricing";
import AboutPublicPage from "@/pages/marketing/About";
import ContactPublicPage from "@/pages/marketing/Contact";
import FeaturesPublicPage from "@/pages/marketing/Features";
import AutopilotPublicPage from "@/pages/marketing/AutopilotPublic";
import BrandLearningPublicPage from "@/pages/marketing/BrandLearningPublic";
import WhatsNewPage from "@/pages/WhatsNew";
import UnsubscribePage from "@/pages/Unsubscribe";
import EmailPreferencesPage from "@/pages/EmailPreferences";
import AdminSubscribersPage from "@/pages/admin/AdminSubscribers";
import AdminAffiliatesPage from "@/pages/admin/AdminAffiliates";
import AffiliateProgramPage from "@/pages/AffiliateProgram";
import AffiliatesPublicPage from "@/pages/marketing/AffiliatesPublic";
import AffiliateDashboardPage from "@/pages/AffiliateDashboard";
import AffiliateAttributionCapture from "@/components/AffiliateAttributionCapture";
import AppShell from "@/components/AppShell";
import ConsentBanner from "@/components/ConsentBanner";
import { useGAPageViewTracking } from "@/hooks/useGAPageViewTracking";
import "@/App.css";

function Protected({ children, skipOnboarding = false, skipPasswordSetup = false, skipPlatformSetup = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--bg-0)] text-[color:var(--text-2)]">
        <div className="font-display text-base">Loading Postmoro…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Iteration 80 Phase 2 — MFA challenge gate. When /auth/me responds
  // with { mfa_required: true } (partial session in the ``mfa_pending``
  // state), no protected route should render until the TOTP verify
  // succeeds. The dedicated /login/2fa route bypasses Protected.
  if (user.mfa_required === true && location.pathname !== "/login/2fa") {
    return <Navigate to="/login/2fa" replace />;
  }
  // Business-Teams V1: if the user just authenticated and there's a
  // pending invite token in sessionStorage, route them to the invite
  // acceptance page ONCE. Skips if we're already there or on any route
  // that must stay reachable during initial account setup.
  if (
    typeof window !== "undefined" &&
    location.pathname !== "/team/accept" &&
    location.pathname !== "/set-password" &&
    location.pathname !== "/onboarding"
  ) {
    const pendingInvite = window.sessionStorage.getItem("pm_pending_invite_token");
    if (pendingInvite) {
      return <Navigate to="/team/accept" replace />;
    }
  }
  // Lazy password-migration gate: legacy accounts created before password
  // auth shipped have needs_password_setup=true. Force them through
  // /set-password before any other protected route renders.
  if (
    !skipPasswordSetup &&
    user.needs_password_setup === true &&
    location.pathname !== "/set-password"
  ) {
    return <Navigate to="/set-password" replace />;
  }
  // First-time users get sent to /onboarding (unless they're already there)
  if (
    !skipOnboarding &&
    user.onboarding_completed === false &&
    location.pathname !== "/onboarding"
  ) {
    return <Navigate to="/onboarding" replace />;
  }
  // Phase 19 — Starter initial-selection gate. When a paid capped plan
  // is active and the user has NOT yet confirmed their 2 platforms
  // (``platform_slots = []`` pinned empty), send them to
  // /settings/connections where the existing "Choose your 2 Starter
  // platforms" picker is rendered. ``skipPlatformSetup`` is passed on
  // routes that must stay reachable during the setup step: Connections
  // itself, Billing (so customers can manage the very subscription
  // that gated them), and Support. OAuth callback, logout, legal
  // pages, and password-setup live outside this Protected wrapper.
  if (
    !skipPlatformSetup &&
    user.needs_platform_setup === true &&
    location.pathname !== "/settings/connections" &&
    location.pathname !== "/settings/admin/connections"
  ) {
    return <Navigate to="/settings/connections" replace />;
  }
  // Business-Teams V1: /billing is owner-only. Members must never be
  // able to view (or read) the owner's Stripe subscription data. The
  // backend enforces the same rule authoritatively via
  // _require_workspace_owner on both read + write billing endpoints;
  // this gate is UX so the member never sees a bare 403 screen.
  if (
    user.workspace_role === "member" &&
    location.pathname.startsWith("/billing")
  ) {
    return <Navigate to="/team" replace />;
  }
  return <AppShell>{children}</AppShell>;
}

function OnboardingRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--bg-0)] text-[color:var(--text-2)]">
        <div className="font-display text-base">Loading Postmoro…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.needs_password_setup === true) return <Navigate to="/set-password" replace />;
  if (user.onboarding_completed) return <Navigate to="/dashboard" replace />;
  return <OnboardingPage />;
}

function SetPasswordRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--bg-0)] text-[color:var(--text-2)]">
        <div className="font-display text-base">Loading Postmoro…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // If the user already has a password set, send them to wherever they belong.
  if (user.needs_password_setup === false) {
    return <Navigate to={user.onboarding_completed ? "/dashboard" : "/onboarding"} replace />;
  }
  return <SetPasswordPage />;
}

function AppRouter() {
  const location = useLocation();
  // Fires a single GA4 page_view per React Router location change
  // (initial mount included). See hooks/useGAPageViewTracking.js
  // for the "one per pathname" guarantee.
  useGAPageViewTracking();
  // CRITICAL: Synchronously detect Emergent OAuth callback (URL fragment with session_id).
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route
          path="/set-password"
          element={
            <SetPasswordRoute />
          }
        />
        {/* Public legal pages — NO auth required (Meta App Review requirement) */}
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/data-deletion" element={<DataDeletionPage />} />
        {/* Public SEO marketing pages (Batch 1). Kept in preview only until
            Meta App Review approves publishing to Facebook + Instagram, at
            which point the qualified paragraphs on each page must be
            revised before deployment. */}
        <Route path="/ai-social-media-management" element={<AiSocialMediaManagementPage />} />
        <Route path="/social-media-scheduler" element={<SocialMediaSchedulerPage />} />
        <Route path="/whats-new" element={<WhatsNewPublicPage />} />
        <Route path="/pricing" element={<PricingPublicPage />} />
        <Route path="/about" element={<AboutPublicPage />} />
        <Route path="/contact" element={<ContactPublicPage />} />
        <Route path="/features" element={<FeaturesPublicPage />} />
        <Route path="/features/autopilot" element={<AutopilotPublicPage />} />
        <Route path="/features/brand-learning" element={<BrandLearningPublicPage />} />
        <Route path="/unsubscribe" element={<UnsubscribePage />} />
        <Route path="/onboarding" element={<OnboardingRoute />} />
        <Route
          path="/dashboard"
          element={
            <Protected>
              <DashboardPage />
            </Protected>
          }
        />
        <Route
          path="/settings/business-profile"
          element={
            <Protected>
              <BusinessProfilePage />
            </Protected>
          }
        />
        <Route
          path="/content"
          element={
            <Protected>
              <ContentManagerPage />
            </Protected>
          }
        />
        <Route
          path="/generate"
          element={
            <Protected>
              <GeneratePage />
            </Protected>
          }
        />
        <Route
          path="/admin/creative-benchmark"
          element={
            <Protected>
              <CreativeBenchmarkPage />
            </Protected>
          }
        />
        <Route
          path="/autopilot"
          element={
            <Protected>
              <AutopilotPage />
            </Protected>
          }
        />
        <Route
          path="/calendar"
          element={
            <Protected>
              <CalendarPage />
            </Protected>
          }
        />
        <Route
          path="/ideas"
          element={
            <Protected>
              <IdeasPage />
            </Protected>
          }
        />
        <Route
          path="/email"
          element={
            <Protected>
              <EmailMarketingPage />
            </Protected>
          }
        />
        <Route
          path="/competitors"
          element={
            <Protected>
              <CompetitorAnalysisPage />
            </Protected>
          }
        />
        <Route
          path="/billing"
          element={
            <Protected skipPlatformSetup>
              <BillingPage />
            </Protected>
          }
        />
        <Route
          path="/settings/connections"
          element={
            <Protected skipPlatformSetup>
              <ConnectionsPage />
            </Protected>
          }
        />
        <Route
          path="/settings/admin/connections"
          element={
            <Protected skipPlatformSetup>
              <ConnectionsPage />
            </Protected>
          }
        />
        <Route
          path="/support"
          element={
            <Protected skipPlatformSetup>
              <SupportPage />
            </Protected>
          }
        />
        <Route
          path="/feedback"
          element={
            <Protected skipPlatformSetup>
              <FeedbackPage />
            </Protected>
          }
        />
        <Route
          path="/updates"
          element={
            <Protected>
              <WhatsNewPage />
            </Protected>
          }
        />
        <Route
          path="/settings/email-preferences"
          element={
            <Protected>
              <EmailPreferencesPage />
            </Protected>
          }
        />
        <Route
          path="/media"
          element={
            <Protected>
              <MediaLibraryPage />
            </Protected>
          }
        />
        {/* Backward-compat alias for older links / bookmarks. New in-app
            links use /media (see /app/frontend/src/pages/Autopilot.js and
            frontend/src/pages/Generate.js). */}
        <Route
          path="/media-library"
          element={
            <Protected>
              <MediaLibraryPage />
            </Protected>
          }
        />
        <Route
          path="/brand-learning"
          element={
            <Protected>
              <BrandLearningPage />
            </Protected>
          }
        />
        <Route
          path="/team"
          element={
            <Protected>
              <TeamPage />
            </Protected>
          }
        />
        <Route path="/team/accept" element={<TeamPage />} />
        <Route
          path="/settings/security"
          element={
            <Protected skipPlatformSetup>
              <SecurityPage />
            </Protected>
          }
        />
        <Route path="/login/2fa" element={<Login2FAPage />} />
        <Route
          path="/admin"
          element={
            <AdminGuard>
              <AdminOverviewPage />
            </AdminGuard>
          }
        />
        <Route
          path="/admin/customers"
          element={
            <AdminGuard>
              <AdminCustomersPage />
            </AdminGuard>
          }
        />
        <Route
          path="/admin/platform-setup"
          element={
            <AdminGuard>
              <AdminPlatformSetupPage />
            </AdminGuard>
          }
        />
        <Route
          path="/admin/operations"
          element={
            <AdminGuard>
              <AdminOperationsPage />
            </AdminGuard>
          }
        />
        <Route
          path="/admin/support"
          element={
            <AdminGuard>
              <AdminSupportPage />
            </AdminGuard>
          }
        />
        <Route
          path="/admin/feedback"
          element={
            <AdminGuard>
              <AdminFeedbackPage />
            </AdminGuard>
          }
        />
        <Route
          path="/admin/meta-checklist"
          element={
            <AdminGuard>
              <AdminMetaChecklistPage />
            </AdminGuard>
          }
        />
        <Route
          path="/admin/linkedin-checklist"
          element={
            <AdminGuard>
              <AdminLinkedInChecklistPage />
            </AdminGuard>
          }
        />
        <Route
          path="/admin/subscribers"
          element={
            <AdminGuard>
              <AdminSubscribersPage />
            </AdminGuard>
          }
        />
        <Route
          path="/admin/affiliates"
          element={
            <AdminGuard>
              <AdminAffiliatesPage />
            </AdminGuard>
          }
        />
        <Route path="/affiliates" element={<AffiliatesPublicPage />} />
        <Route path="/affiliate-program" element={<AffiliateProgramPage />} />
        <Route
          path="/affiliate"
          element={
            <Protected>
              <AffiliateDashboardPage />
            </Protected>
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </AnimatePresence>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AffiliateAttributionCapture />
        <AppRouter />
        <ConsentBanner />
        <Toaster richColors closeButton position="bottom-right" theme="dark" />
      </AuthProvider>
    </BrowserRouter>
  );
}
